import "./styles/main.scss";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { InitialRouteProvider } from "./context/InitialRouteContext";
import Header from "./components/Header";
// import Footer from "./components/Footer"; // Unused for now
import { ThemeProvider } from "./context/ThemeContext";
import HeadingAnimator from "./components/HeadingAnimator";
import CookieBanner from "./components/CookieBanner";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import HotjarInit from "./components/HotjarInit";
import DocumentTitleTabCycle from "./components/DocumentTitleTabCycle";
import SvgPathDrawOnLoad from "./components/SvgPathDrawOnLoad";
import Loader from "./components/Loader";
import LoaderBootstrap from "./components/LoaderBootstrap";
import { registerLoaderEndDebug } from "./utils/loaderEndDebug";
import i18n from "./i18n";

registerLoaderEndDebug();

// Lazy load routes - only Home is loaded immediately
const Home = lazy(() => import("./pages/Home"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BrandingLogoArticle = lazy(() => import("./pages/BrandingLogoArticle"));
const ProjektowanieLogoDlaPolskichFirmArticle = lazy(() =>
  import("./pages/ProjektowanieLogoDlaPolskichFirmKluczDoSukcesuNaRodzimymRynkuArticle")
);
const DlaczegoTwJWordpressArticle = lazy(() =>
  import("./pages/DlaczegoTwJWordpressZabijaWiarygodnoTwojejFirmyB2bICzymGoZastPiArticle")
);
const BlogKtorySprzedajeArticle = lazy(() =>
  import("./pages/BlogKtorySprzedajeDlaczegoWiekszoscFirmUsugowychRobiToZleArticle")
);
const PrzyszoscBrandinguArticle = lazy(() =>
  import("./pages/PrzyszoscBrandinguWPolsce5ZmianNadchodzacychW2026RokuArticle")
);
const PsychologiaKoloruWB2bCoBarwyTwojejStronyMowiaOTwoimBiznesieArticle = lazy(() =>
  import("./pages/PsychologiaKoloruWB2bCoBarwyTwojejStronyMowiaOTwoimBiznesieArticle")
);
const FigmaVsKodDlaczegoW2026RokuDesignerzyMuszaRozumiecDeveloperaIViceVersaArticle = lazy(() =>
  import("./pages/FigmaVsKodDlaczegoW2026RokuDesignerzyMuszaRozumiecDeveloperaIViceVersaArticle")
);
const Wiedza = lazy(() => import("./pages/Wiedza"));
const RebrandingPolskiejFirmyRodzinnejJakZmienicWizerunekNieTracacTradycjiArticle = lazy(() =>
  import("./pages/RebrandingPolskiejFirmyRodzinnejJakZmienicWizerunekNieTracacTradycjiArticle")
);
const EfektAureoliHaloEffectWB2bDlaczegoKlienciOceniajaTwojeKompetencjePoStopceWMailuArticle = lazy(() =>
  import("./pages/EfektAureoliHaloEffectWB2bDlaczegoKlienciOceniajaTwojeKompetencjePoStopceWMailuArticle")
);
const RolaUxWTworzeniuStronKluczowyCzynnikArticle = lazy(() =>
  import("./pages/RolaUxWTworzeniuStronKluczowyCzynnikArticle")
);
const StrategiaSocialMediaDlaB2bWidocznoscIAutorytetArticle = lazy(() =>
  import("./pages/StrategiaSocialMediaDlaB2bWidocznoscIAutorytetArticle")
);
const AgencjaUxJakWybracWsparcieDlaSkutecznegoRedesignuArticle = lazy(() =>
  import("./pages/AgencjaUxJakWybracWsparcieDlaSkutecznegoRedesignuArticle")
);
const ProjektowanieSklepowInternetowychPrzewodnikDlaFirmArticle = lazy(() =>
  import("./pages/ProjektowanieSklepowInternetowychPrzewodnikDlaFirmArticle")
);
const RolaUiWSklepachOnlineJakPodniescKonwersjeArticle = lazy(() =>
  import("./pages/RolaUiWSklepachOnlineJakPodniescKonwersjeArticle")
);
const NotFound = lazy(() => import("./pages/NotFound"));

// Language route wrapper - syncs URL path with i18n language
const LanguageRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathLang = location.pathname.startsWith('/en') ? 'en' : 'pl';
  // Check if language matches path on initial render
  // Default to true for Polish paths (most common case)
  const initialMatch = pathLang === 'pl' ? true : (i18n.language === 'en');
  const [isLanguageReady, setIsLanguageReady] = React.useState(initialMatch);
  const previousPathRef = React.useRef(location.pathname);
  const hasCheckedInitialRedirect = React.useRef(false);
  
  useEffect(() => {
    // Redirect root path to /en if no language preference is saved and default is English
    // Only check once on initial mount
    if (!hasCheckedInitialRedirect.current && location.pathname === '/') {
      hasCheckedInitialRedirect.current = true;
      try {
        const savedLang = localStorage.getItem("i18nextLng");
        if (!savedLang) {
          // No saved language preference, stay on root (default Polish)
          return;
        } else if (savedLang === 'en') {
          // User has English preference, redirect to /en
          navigate('/en', { replace: true });
          return;
        } else {
          // User has Polish preference, stay on root (Polish routes)
          return;
        }
      } catch {
        // If localStorage fails, stay on root (default Polish)
        return;
      }
    }
    
    // Check if i18n language doesn't match the path - this indicates an intentional language switch
    const i18nLang = i18n.language;
    const pathLangFromPath = location.pathname.startsWith('/en') ? 'en' : 'pl';
    
    // If i18n language doesn't match path, prioritize i18n language (user clicked language switcher)
    if (i18nLang !== pathLangFromPath) {
      // User intentionally switched language, update path to match
      const wasOnEnglish = previousPathRef.current.startsWith('/en');
      const currentPath = location.pathname;
      const basePath = wasOnEnglish ? currentPath.replace('/en', '') || '/' : currentPath;
      
      if (i18nLang === 'en') {
        // Switching to English - add /en prefix
        const newPath = `/en${basePath === '/' ? '' : basePath}${location.search}${location.hash}`;
        if (newPath !== currentPath) {
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      } else {
        // Switching to Polish - remove /en prefix
        if (currentPath.startsWith('/en')) {
          const newPath = `${basePath}${location.search}${location.hash}`;
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      }
    }
    
    // If navigating from /en to a path without /en (and not an intentional language switch), preserve the /en prefix
    const wasOnEnglish = previousPathRef.current.startsWith('/en');
    const isNowOnEnglish = location.pathname.startsWith('/en');
    
    if (wasOnEnglish && !isNowOnEnglish && location.pathname !== '/' && i18nLang === 'en') {
      // User was on English version and still wants English, redirect to English version of this path
      const newPath = `/en${location.pathname}${location.search}${location.hash}`;
      previousPathRef.current = newPath;
      navigate(newPath, { replace: true });
      return;
    }
    
    // Update previous path reference
    previousPathRef.current = location.pathname;
    
    // If language matches path, we're ready
    if (i18n.language === pathLang) {
      setIsLanguageReady(true);
      return;
    }
    
    // If path is Polish and i18n is Polish, we're ready (even if not explicitly matched above)
    if (pathLang === 'pl' && i18n.language === 'pl') {
      setIsLanguageReady(true);
      return;
    }
    
    setIsLanguageReady(false);
    
    // If switching to English, ensure translations are loaded first
    if (pathLang === 'en') {
      // Check if English is already loaded
      if (!i18n.hasResourceBundle('en', 'translation')) {
        // Load English translations first, then change language
        import("./locales/en.json")
          .then((en) => {
            i18n.addResourceBundle('en', 'translation', en.default || en);
            return i18n.changeLanguage('en');
          })
          .then(() => {
            try {
              localStorage.setItem("i18nextLng", 'en');
            } catch {}
            setIsLanguageReady(true);
          })
          .catch(() => {
            console.warn("Failed to load English translations");
            // Fallback to Polish
            i18n.changeLanguage('pl').then(() => {
              setIsLanguageReady(true);
            });
          });
      } else {
        // English already loaded, just switch
        i18n.changeLanguage('en').then(() => {
          try {
            localStorage.setItem("i18nextLng", 'en');
          } catch {}
          setIsLanguageReady(true);
        });
      }
    } else {
      // Switch to Polish
      i18n.changeLanguage('pl').then(() => {
        try {
          localStorage.setItem("i18nextLng", 'pl');
        } catch {}
        setIsLanguageReady(true);
      });
    }
  }, [location.pathname, location.search, location.hash, pathLang, navigate]);
  
  // Show loader while language is being switched
  // For Polish paths, always render immediately (don't wait for language sync)
  // Only show loader for English paths that need translation loading
  if (pathLang === 'pl') {
    return <>{children}</>;
  }
  
  if (!isLanguageReady && pathLang === 'en') {
    return <Loader />;
  }
  
  return <>{children}</>;
};

// Lazy load Swiper CSS only when needed (will be imported in components that use it)
// Removed global Swiper CSS imports - will be loaded on-demand
 
// Removed glitch-related components
// import CareGlitchLoop from "./components/CareGlitchLoop";
// import AssignGlitchHalf from "./components/AssignGlitchHalf";
// import TargetedGlitch from "./components/TargetedGlitch";
// import SvgGlitchLoop from "./components/SvgGlitchLoop";
// import BannerifyHeadings from "./components/BannerifyHeadings";

// Removed Reset/Matrix effects

const App = () => (
  <HelmetProvider>
    <ThemeProvider>
      <CookieConsentProvider>
        <Router>
          <InitialRouteProvider>
            <LanguageRoute>
              <div className="app">
                  <LoaderBootstrap />
                  <HotjarInit />
                  <DocumentTitleTabCycle />
                  {/* Global path draw animator (listens for loader:finished) */}
                  <SvgPathDrawOnLoad />
                  <Header />
                  <Suspense fallback={<div className="route-loading-fallback" style={{ minHeight: "60vh" }} aria-hidden="true" />}>
                    <Routes>
                      {/* Polish routes */}
                      <Route path="/" element={<Home />} />
                      <Route path="/home" element={<Home />} />
                      <Route path="/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/wiedza" element={<Wiedza />} />
                      <Route path="/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" element={<BrandingLogoArticle />} />
                      <Route
                        path="/wiedza/branding/projektowanie-logo-dla-polskich-firm-klucz-do-sukcesu-na-rodzimym-rynku"
                        element={<ProjektowanieLogoDlaPolskichFirmArticle />}
                      />
                      <Route
                        path="/wiedza/branding/dlaczego-tw-j-wordpress-zabija-wiarygodno-twojej-firmy-b2b-i-czym-go-zast-pi"
                        element={<DlaczegoTwJWordpressArticle />}
                      />
                      <Route
                        path="/wiedza/branding/blog-ktory-sprzedaje-dlaczego-wiekszosc-firm-usugowych-robi-to-zle"
                        element={<BlogKtorySprzedajeArticle />}
                      />
                      <Route
                        path="/wiedza/branding/przyszosc-brandingu-w-polsce-5-zmian-nadchodzacych-w-2026-roku"
                        element={<PrzyszoscBrandinguArticle />}
                      />
                      <Route
                        path="/wiedza/branding/psychologia-koloru-w-b2b-co-barwy-twojej-strony-mowia-o-twoim-biznesie"
                        element={<PsychologiaKoloruWB2bCoBarwyTwojejStronyMowiaOTwoimBiznesieArticle />}
                      />
                      <Route
                        path="/wiedza/branding/figma-vs-kod-dlaczego-w-2026-roku-designerzy-musza-rozumiec-developera-i-vice-versa"
                        element={<FigmaVsKodDlaczegoW2026RokuDesignerzyMuszaRozumiecDeveloperaIViceVersaArticle />}
                      />
                      <Route
                        path="/wiedza/branding/rebranding-polskiej-firmy-rodzinnej-jak-zmienic-wizerunek-nie-tracac-tradycji"
                        element={<RebrandingPolskiejFirmyRodzinnejJakZmienicWizerunekNieTracacTradycjiArticle />}
                      />
                      <Route
                        path="/wiedza/branding/efekt-aureoli-halo-effect-w-b2b-dlaczego-klienci-oceniaja-twoje-kompetencje-po-stopce-w-mailu"
                        element={<EfektAureoliHaloEffectWB2bDlaczegoKlienciOceniajaTwojeKompetencjePoStopceWMailuArticle />}
                      />
                      <Route
                        path="/wiedza/branding/rola-ux-w-tworzeniu-stron-kluczowy-czynnik"
                        element={<RolaUxWTworzeniuStronKluczowyCzynnikArticle />}
                      />
                      <Route
                        path="/wiedza/branding/strategia-social-media-dla-b2b-widocznosc-i-autorytet"
                        element={<StrategiaSocialMediaDlaB2bWidocznoscIAutorytetArticle />}
                      />
                      <Route
                        path="/wiedza/branding/agencja-ux-jak-wybrac-wsparcie-dla-skutecznego-redesignu"
                        element={<AgencjaUxJakWybracWsparcieDlaSkutecznegoRedesignuArticle />}
                      />
                      <Route
                        path="/wiedza/branding/projektowanie-sklepow-internetowych-przewodnik-dla-firm"
                        element={<ProjektowanieSklepowInternetowychPrzewodnikDlaFirmArticle />}
                      />
                      <Route
                        path="/wiedza/branding/rola-ui-w-sklepach-online-jak-podniesc-konwersje"
                        element={<RolaUiWSklepachOnlineJakPodniescKonwersjeArticle />}
                      />

                      {/* English routes */}
                      <Route path="/en" element={<Home />} />
                      <Route path="/en/home" element={<Home />} />
                      <Route path="/en/cookie-policy" element={<CookiePolicy />} />
                      <Route path="/en/wiedza" element={<Wiedza />} />
                      <Route path="/en/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek" element={<BrandingLogoArticle />} />
                      <Route
                        path="/en/wiedza/branding/projektowanie-logo-dla-polskich-firm-klucz-do-sukcesu-na-rodzimym-rynku"
                        element={<ProjektowanieLogoDlaPolskichFirmArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/dlaczego-tw-j-wordpress-zabija-wiarygodno-twojej-firmy-b2b-i-czym-go-zast-pi"
                        element={<DlaczegoTwJWordpressArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/blog-ktory-sprzedaje-dlaczego-wiekszosc-firm-usugowych-robi-to-zle"
                        element={<BlogKtorySprzedajeArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/przyszosc-brandingu-w-polsce-5-zmian-nadchodzacych-w-2026-roku"
                        element={<PrzyszoscBrandinguArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/psychologia-koloru-w-b2b-co-barwy-twojej-strony-mowia-o-twoim-biznesie"
                        element={<PsychologiaKoloruWB2bCoBarwyTwojejStronyMowiaOTwoimBiznesieArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/figma-vs-kod-dlaczego-w-2026-roku-designerzy-musza-rozumiec-developera-i-vice-versa"
                        element={<FigmaVsKodDlaczegoW2026RokuDesignerzyMuszaRozumiecDeveloperaIViceVersaArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/rebranding-polskiej-firmy-rodzinnej-jak-zmienic-wizerunek-nie-tracac-tradycji"
                        element={<RebrandingPolskiejFirmyRodzinnejJakZmienicWizerunekNieTracacTradycjiArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/efekt-aureoli-halo-effect-w-b2b-dlaczego-klienci-oceniaja-twoje-kompetencje-po-stopce-w-mailu"
                        element={<EfektAureoliHaloEffectWB2bDlaczegoKlienciOceniajaTwojeKompetencjePoStopceWMailuArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/rola-ux-w-tworzeniu-stron-kluczowy-czynnik"
                        element={<RolaUxWTworzeniuStronKluczowyCzynnikArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/strategia-social-media-dla-b2b-widocznosc-i-autorytet"
                        element={<StrategiaSocialMediaDlaB2bWidocznoscIAutorytetArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/agencja-ux-jak-wybrac-wsparcie-dla-skutecznego-redesignu"
                        element={<AgencjaUxJakWybracWsparcieDlaSkutecznegoRedesignuArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/projektowanie-sklepow-internetowych-przewodnik-dla-firm"
                        element={<ProjektowanieSklepowInternetowychPrzewodnikDlaFirmArticle />}
                      />
                      <Route
                        path="/en/wiedza/branding/rola-ui-w-sklepach-online-jak-podniesc-konwersje"
                        element={<RolaUiWSklepachOnlineJakPodniescKonwersjeArticle />}
                      />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
            </Suspense>
            <HeadingAnimator />
            <CookieBanner />
                  </div>
                </LanguageRoute>
        </InitialRouteProvider>
      </Router>
      </CookieConsentProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
